import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Plus, RefreshCw, Eye, Pencil, Send } from 'lucide-react';
import api from '../../../services/api';
import config from '../../../config/config';
import LoadingSpinner from '../../common/LoadingSpinner';
import { toast } from 'react-toastify';

const STATUS_TABS = [
  { value: '', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

const statusBadge = (status) => {
  const map = {
    draft: 'bg-slate-100 text-slate-700',
    pending: 'bg-amber-100 text-amber-900',
    approved: 'bg-emerald-100 text-emerald-800',
    rejected: 'bg-red-100 text-red-800',
  };
  return map[status] || 'bg-slate-100 text-slate-600';
};

async function openOfferPdf(id, preview = false) {
  const token = localStorage.getItem(config.TOKEN_STORAGE_KEY);
  const q = preview ? '?preview=true' : '';
  const res = await fetch(`${config.API_BASE_URL}/admin/offer-letters/${id}/pdf${q}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to load PDF');
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener,noreferrer');
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

const OfferLetterList = () => {
  const [status, setStatus] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingId, setSendingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = status ? `?status=${encodeURIComponent(status)}` : '';
      const res = await api.get(`/admin/offer-letters${params}`);
      setRows(res.data?.offerLetters || []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load offer letters');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  const handlePreview = async (id, preview) => {
    try {
      await openOfferPdf(id, preview);
    } catch (e) {
      toast.error(e.message || 'Failed to open PDF');
    }
  };

  const handleSend = async (row) => {
    if (sendingId) return;
    setSendingId(row._id);
    try {
      const res = await api.post(`/admin/offer-letters/${row._id}/send`);
      toast.success(res.data?.message || `Offer letter sent to ${row.email}`);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to send offer letter');
    } finally {
      setSendingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4 rounded-3xl border border-slate-200 bg-gradient-to-r from-white to-slate-50/80 p-5 shadow-sm sm:p-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">Offer Letters</h1>
          <p className="mt-1 text-sm text-slate-500">Create, preview, and submit offer letters for Super Admin approval.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm font-medium text-slate-600 shadow-sm hover:border-indigo-200 hover:text-indigo-700"
          >
            <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <Link
            to="/admin/offer-letters/new"
            className="inline-flex items-center rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-indigo-700"
          >
            <Plus className="mr-1.5 h-4 w-4" />
            New offer letter
          </Link>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 rounded-2xl border border-slate-200 bg-white/90 p-2 shadow-sm">
        {STATUS_TABS.map((t) => (
          <button
            key={t.value || 'all'}
            type="button"
            onClick={() => setStatus(t.value)}
            className={`rounded-xl px-4 py-2.5 text-sm font-medium transition-colors ${
              status === t.value
                ? 'bg-indigo-600 text-white shadow-sm'
                : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white/90 shadow-sm">
        {loading ? (
          <LoadingSpinner size="lg" className="py-12" />
        ) : rows.length === 0 ? (
          <div className="py-16 text-center">
            <FileText className="mx-auto h-10 w-10 text-slate-300" />
            <p className="mt-3 text-sm text-slate-500">No offer letters in this category.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50/90">
                <tr>
                  {['Candidate', 'Job title', 'Email', 'CTC (LPA)', 'Status', 'Updated', ''].map((h) => (
                    <th
                      key={h || 'actions'}
                      className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((row) => (
                  <tr key={row._id} className="hover:bg-slate-50/70">
                    <td className="px-4 py-3 text-sm font-medium text-slate-900">
                      {row.salutation} {row.candidateName}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{row.position}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{row.email}</td>
                    <td className="px-4 py-3 text-sm tabular-nums text-slate-600">
                      {row.computedCtcLpa != null ? `Rs ${Number(row.computedCtcLpa).toFixed(2)}` : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusBadge(row.status)}`}>
                        {row.status}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-500">
                      {row.updatedAt
                        ? new Date(row.updatedAt).toLocaleDateString('en-IN')
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          title="Preview PDF"
                          onClick={() => handlePreview(row._id, true)}
                          className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-indigo-600"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        {row.status === 'approved' && (
                          <button
                            type="button"
                            title="Send offer letter"
                            disabled={sendingId === row._id}
                            onClick={() => handleSend(row)}
                            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-indigo-600 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <Send className={`h-4 w-4 ${sendingId === row._id ? 'animate-pulse' : ''}`} />
                          </button>
                        )}
                        {['draft', 'rejected'].includes(row.status) && (
                          <Link
                            to={`/admin/offer-letters/${row._id}`}
                            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-indigo-600"
                            title="Edit"
                          >
                            <Pencil className="h-4 w-4" />
                          </Link>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default OfferLetterList;
export { openOfferPdf };
